/**
 * Helpers puros usados pelas mutations de relacionamento e pelos controllers.
 *
 * Sem side-effects (sem dispatch, sem chamadas HTTP). Toda lógica de walker
 * sobre as 4 slices onde um cliente de relacionamento aparece fica isolada
 * aqui — mutations.js só faz dispatch da iteração.
 *
 * Slices walkadas:
 *   1. `state.clients.data`              — grupos `[{ identificador, clientes[], total }]`
 *   2. `state.clientsTasks.data`         — grupos `[{ identificador, tarefas[{ cliente }] }]`
 *   3. `state.relationshipKanban.data`   — etapas `[{ id, clientes[] }]`
 *   4. `state.monthlyRoutine.data`       — stages `[{ id, clientes[], total_clientes }]`
 */

export const normalizeClienteId = (cliente) => {
  if (!cliente) return null
  return cliente.id_cliente_app || null
}

const getArray = (value) => (Array.isArray(value) ? value : [])

const matchesClienteId = (cliente, idClienteApp) =>
  cliente && normalizeClienteId(cliente) === idClienteApp

// O backend do kanban de relacionamento ora retorna `etapa.clients` (inglês),
// ora `etapa.clientes` (português) — view de kanban faz fallback no mesmo
// padrão. Helper único pra acessar a chave correta sem espalhar a
// ambiguidade pelo walker.
const getKanbanClientes = (etapa) => {
  if (!etapa) return null
  if (Array.isArray(etapa.clients)) return etapa.clients
  if (Array.isArray(etapa.clientes)) return etapa.clientes
  return null
}

export const forEachClienteInState = (state, idClienteApp, cb) => {
  if (!state || !idClienteApp || typeof cb !== 'function') return

  const clientGroups = getArray(state.clients && state.clients.data)
  clientGroups.forEach((grupo) => {
    if (!grupo) return
    const clientes = getArray(grupo.clientes)
    clientes.forEach((cliente, index) => {
      if (!matchesClienteId(cliente, idClienteApp)) return
      cb(cliente, {
        slice: 'clients',
        container: clientes,
        index,
        grupo,
      })
    })
  })

  const tasksGroups = getArray(state.clientsTasks && state.clientsTasks.data)
  tasksGroups.forEach((grupo) => {
    if (!grupo) return
    const tarefas = getArray(grupo.tarefas)
    tarefas.forEach((tarefa) => {
      if (!tarefa) return
      if (!matchesClienteId(tarefa.cliente, idClienteApp)) return
      cb(tarefa.cliente, {
        slice: 'clientsTasks',
        tarefa,
        grupo,
      })
    })
  })

  const kanbanEtapas = getArray(
    state.relationshipKanban && state.relationshipKanban.data
  )
  kanbanEtapas.forEach((etapa) => {
    if (!etapa) return
    const clientes = getKanbanClientes(etapa)
    if (!clientes) return
    clientes.forEach((cliente, index) => {
      if (!matchesClienteId(cliente, idClienteApp)) return
      cb(cliente, {
        slice: 'relationshipKanban',
        container: clientes,
        index,
        etapa,
      })
    })
  })

  const monthlyStages = getArray(
    state.monthlyRoutine && state.monthlyRoutine.data
  )
  monthlyStages.forEach((stage) => {
    if (!stage) return
    const clientes = getArray(stage.clientes)
    clientes.forEach((cliente, index) => {
      if (!matchesClienteId(cliente, idClienteApp)) return
      cb(cliente, {
        slice: 'monthlyRoutine',
        container: clientes,
        index,
        stage,
      })
    })
  })
}

/**
 * Aplica patch raso (key-by-key) num objeto cliente. Trata `relacionamento`
 * como objeto aninhado que merge, e ignora `undefined` no patch.
 */
export const applyPatchToCliente = (cliente, patch) => {
  if (!cliente || !patch || typeof patch !== 'object') return
  Object.keys(patch).forEach((key) => {
    const value = patch[key]
    if (value === undefined) return
    const isRelacionamentoMerge =
      key === 'relacionamento' &&
      cliente.relacionamento &&
      typeof cliente.relacionamento === 'object' &&
      value &&
      typeof value === 'object'
    if (isRelacionamentoMerge) {
      cliente.relacionamento = { ...cliente.relacionamento, ...value }
      return
    }
    cliente[key] = value
  })
}

export const findClienteGroupIdentifier = (clientGroups, idClienteApp) => {
  if (!Array.isArray(clientGroups) || !idClienteApp) return null
  for (const grupo of clientGroups) {
    if (!grupo) continue
    const clientes = getArray(grupo.clientes)
    const found = clientes.some((cliente) =>
      matchesClienteId(cliente, idClienteApp)
    )
    if (found) return grupo.identificador
  }
  return null
}

export const removeClienteFromAllClientGroups = (
  clientGroups,
  idClienteApp
) => {
  if (!Array.isArray(clientGroups) || !idClienteApp) return null
  let removed = null
  clientGroups.forEach((grupo) => {
    if (!grupo || !Array.isArray(grupo.clientes)) return
    const index = grupo.clientes.findIndex((cliente) =>
      matchesClienteId(cliente, idClienteApp)
    )
    if (index === -1) return
    removed = grupo.clientes[index]
    grupo.clientes.splice(index, 1)
    grupo.total = grupo.clientes.length
  })
  return removed
}

export const upsertClienteInClientGroup = ({
  clientGroups,
  identificador,
  cliente,
}) => {
  if (!Array.isArray(clientGroups) || !identificador || !cliente) return false
  const grupo = clientGroups.find((g) => g && g.identificador === identificador)
  if (!grupo) return false
  if (!Array.isArray(grupo.clientes)) grupo.clientes = []
  const idClienteApp = normalizeClienteId(cliente)
  const existingIndex = grupo.clientes.findIndex((c) =>
    matchesClienteId(c, idClienteApp)
  )
  if (existingIndex === -1) {
    grupo.clientes.push(cliente)
  } else {
    grupo.clientes.splice(existingIndex, 1, cliente)
  }
  grupo.total = grupo.clientes.length
  return true
}

const removeTasksByClienteFromGroup = (grupo, idClienteApp) => {
  if (!grupo || !Array.isArray(grupo.tarefas)) return
  grupo.tarefas = grupo.tarefas.filter(
    (tarefa) => !matchesClienteId(tarefa && tarefa.cliente, idClienteApp)
  )
}

export const removeClienteTasksFromAllGroups = (taskGroups, idClienteApp) => {
  if (!Array.isArray(taskGroups) || !idClienteApp) return
  taskGroups.forEach((grupo) =>
    removeTasksByClienteFromGroup(grupo, idClienteApp)
  )
}

export const mergeTaskGroupsForCliente = ({
  existingTaskGroups,
  freshTaskGroups,
  idClienteApp,
}) => {
  if (!Array.isArray(existingTaskGroups) || !idClienteApp) return
  removeClienteTasksFromAllGroups(existingTaskGroups, idClienteApp)
  if (!Array.isArray(freshTaskGroups)) return
  freshTaskGroups.forEach((freshGrupo) => {
    if (!freshGrupo || !freshGrupo.identificador) return
    const tarefas = getArray(freshGrupo.tarefas).filter((tarefa) =>
      matchesClienteId(tarefa && tarefa.cliente, idClienteApp)
    )
    if (!tarefas.length) return
    const target = existingTaskGroups.find(
      (g) => g && g.identificador === freshGrupo.identificador
    )
    if (!target) return
    if (!Array.isArray(target.tarefas)) target.tarefas = []
    target.tarefas.push(...tarefas)
  })
}

const removeClienteFromEtapaArray = (etapa, idClienteApp) => {
  const clientes = getKanbanClientes(etapa)
  if (!clientes) return null
  const index = clientes.findIndex((cliente) =>
    matchesClienteId(cliente, idClienteApp)
  )
  if (index === -1) return null
  const [removed] = clientes.splice(index, 1)
  return removed
}

// Resolve qual chave (`clients` ou `clientes`) o objeto da etapa usa pra
// armazenar os cards. Se nenhuma das duas existir como array, retorna
// 'clientes' (default português) — o caller é responsável por criar a chave.
const resolveKanbanClientesKey = (etapa) => {
  if (Array.isArray(etapa && etapa.clients)) return 'clients'
  if (Array.isArray(etapa && etapa.clientes)) return 'clientes'
  return 'clientes'
}

export const mergeKanbanForCliente = ({
  existingEtapas,
  freshEtapas,
  idClienteApp,
}) => {
  if (!Array.isArray(existingEtapas) || !idClienteApp) return
  existingEtapas.forEach((etapa) =>
    removeClienteFromEtapaArray(etapa, idClienteApp)
  )
  if (!Array.isArray(freshEtapas)) return
  freshEtapas.forEach((freshEtapa) => {
    if (!freshEtapa || !freshEtapa.id) return
    const freshClientes = (getKanbanClientes(freshEtapa) || []).filter(
      (cliente) => matchesClienteId(cliente, idClienteApp)
    )
    if (!freshClientes.length) return
    const target = existingEtapas.find((e) => e && e.id === freshEtapa.id)
    if (!target) return
    const key = resolveKanbanClientesKey(target)
    if (!Array.isArray(target[key])) target[key] = []
    target[key].push(...freshClientes)
  })
}

export const computeSummaryFromClientGroups = (clientGroups) => {
  if (!Array.isArray(clientGroups)) return {}
  const totais = {}
  clientGroups.forEach((grupo) => {
    if (!grupo || !grupo.identificador) return
    const clientes = getArray(grupo.clientes)
    totais[grupo.identificador] = clientes.length
  })
  return totais
}
